import { api } from "@/api/axios.ts";
import { EmployeeMentionList } from "@/components/rich-editor/extensions/EmployeeMentionList.tsx";
import {
  ApiEmployee,
  ApiEmployeeFilter,
  getActiveEmployeeLifecycleStatuses,
} from "@/types/employees/employee.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import { Mention } from "@tiptap/extension-mention";
import { ReactRenderer } from "@tiptap/react";

export const EmployeeMentions = Mention.configure({
  suggestion: {
    char: "@",
    items: async ({ query }) => {
      const { data: employees } = await api.post<ApiPagedData<ApiEmployee>>(
        "/api/v1/employees/search",
        {
          text: query,
          page_number: 1,
          employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
        } as ApiEmployeeFilter
      );

      return employees?.items ?? [];
    },
    render: () => {
      let component: ReactRenderer<ApiEmployee[]> | null = null;
      let floatingPosition: { x: number; y: number } | null = null;

      return {
        onStart: (props) => {
          const { clientRect } = props;
          const { editor, range } = props;

          if (!clientRect) return;

          floatingPosition = {
            x: clientRect()?.left ?? 0,
            y: (clientRect()?.bottom ?? 0) + 5,
          };

          component = new ReactRenderer(EmployeeMentionList, {
            props: {
              ...props,
              position: floatingPosition,
              editor,
              range,
              insert(item: ApiEmployee, text: string) {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .insertContent({
                    type: "mention",
                    attrs: {
                      id: item.id,
                      label: text,
                    },
                  })
                  .setTextSelection(range.from + 1)
                  .run();
              },
            },
            editor: props.editor,
          });
        },

        onUpdate(props) {
          const { clientRect } = props;

          if (!clientRect) return;

          floatingPosition = {
            x: clientRect()?.left ?? 0,
            y: (clientRect()?.bottom ?? 0) + 5,
          };

          component?.updateProps({
            ...props,
            position: floatingPosition,
            insert(item: ApiEmployee, text: string) {
              const { editor, range } = props;

              editor
                .chain()
                .focus()
                .deleteRange(range)
                .insertContent({
                  type: "mention",
                  attrs: {
                    id: item.id,
                    label: text,
                  },
                })
                .setTextSelection(range.from + 1)
                .run();
            },
          });
        },

        onKeyDown(props) {
          if (props.event.key === "Enter" && component) {
            return true;
          }

          if (props.event.key === "Escape") {
            component?.destroy();
            floatingPosition = null;
            return true;
          }

          return false;
        },

        onExit() {
          component?.destroy();
          floatingPosition = null;
        },
      };
    },
  },
  HTMLAttributes: {
    class: "mention",
  },
  renderText: ({ node }) => `@${node.attrs.label}`,
});
